import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { ShoppingCart, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES: { value: string; label: string; helper: string }[] = [
  { value: 'topup', label: 'Listing Credits', helper: 'Hot, Super Hot, Refresh, Signature, Story, Video Tour, Featured Project — buy what you need.' },
  { value: 'lead_wallet', label: 'Lead Packs', helper: 'Prepay your lead wallet and pay only for qualified buyer leads.' },
  { value: 'banner', label: 'Banner Ads', helper: 'Self-serve homepage and listings sidebar banners.' },
  { value: 'branding', label: 'Branding', helper: 'Verified badges and premium agency branding.' },
];

export default function CreditStore() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const initialTab = (params.get('tab') === 'leads' ? 'lead_wallet' : params.get('tab')) || 'topup';
  const [tab, setTab] = useState(initialTab);
  const [busy, setBusy] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['topup-packages', tab],
    queryFn: async () => {
      const { data } = await supabase
        .from('advertising_packages' as any)
        .select('id, sku, name, description, price_etb, credit_grants, duration_days, category')
        .eq('category', tab)
        .eq('is_active', true)
        .order('price_etb');
      return (data as any[]) || [];
    },
  });

  const purchase = async (pkgId: string, sku: string) => {
    if (!user) return toast.error('Please sign in first');
    setBusy(pkgId);
    const { data, error } = await supabase.rpc('purchase_topup' as any, {
      _package_id: pkgId, _provider: 'manual',
    });
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success(`Order placed (${sku}). Pay your invoice to activate.`);
    qc.invalidateQueries({ queryKey: ['my-invoices'] });
    window.location.href = '/dashboard/my-invoices';
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShoppingCart className="h-6 w-6 text-primary" /> Credit Store
        </h1>
        <p className="text-sm text-muted-foreground">Buy à la carte credits, lead packs, banner ads, and premium branding.</p>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setParams({ tab: v }); }}>
        <TabsList className="flex-wrap h-auto">
          {CATEGORIES.map((c) => <TabsTrigger key={c.value} value={c.value}>{c.label}</TabsTrigger>)}
        </TabsList>

        {CATEGORIES.map((c) => (
          <TabsContent key={c.value} value={c.value} className="space-y-3">
            <p className="text-sm text-muted-foreground">{c.helper}</p>
            {isLoading ? <Skeleton className="h-40" /> : !data?.length ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">No packages available right now.</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.map((p) => {
                  const grants = p.credit_grants && Object.keys(p.credit_grants).length > 0
                    ? Object.entries(p.credit_grants).map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(', ')
                    : null;
                  return (
                    <Card key={p.id} className="relative hover:shadow-md transition-shadow">
                      <CardContent className="p-5 space-y-3">
                        <Badge variant="outline" className="text-[10px]">{p.sku}</Badge>
                        <p className="font-semibold">{p.name}</p>
                        <p className="text-2xl font-bold">{Number(p.price_etb).toLocaleString()}<span className="text-xs font-normal text-muted-foreground"> ETB</span></p>
                        {grants && <p className="text-xs text-muted-foreground flex items-center gap-1"><Sparkles className="h-3 w-3 text-primary" /> {grants}</p>}
                        {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}
                        <Button size="sm" className="w-full" disabled={busy === p.id}
                          onClick={() => purchase(p.id, p.sku)}>
                          {busy === p.id ? 'Placing order...' : 'Buy now'}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
